// Contrastverhouding tussen twee kleuren volgens WCAG 2.1.
//
// Waarvoor: de toegankelijkheid van de open vragenlijst. De norm vraagt 4.5:1
// voor gewone tekst en 3:1 voor grote tekst (vanaf 24px, of 18.66px vetgedrukt)
// en voor de randen van bedieningselementen.
//
// Werkt alleen op ondoorzichtige hex-kleuren. Een halfdoorzichtige laag of een
// achtergrond-AFBEELDING kun je hier niet doorheen halen — dan is de echte
// achtergrondkleur niet bekend en moet je per pixel meten. Vandaar dat een
// onleesbare invoer een fout geeft in plaats van stil 1 terug te geven: een
// controle die stil doorloopt, meet niets meer.

/**
 * @param {string} hex - '#rgb' of '#rrggbb'
 * @returns {{r: number, g: number, b: number}}
 */
const parseHex = (hex) => {
    const value = String(hex ?? '').trim();
    const kort = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(value);
    const lang = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(value);

    if (kort) {
        return {
            r: parseInt(kort[1] + kort[1], 16),
            g: parseInt(kort[2] + kort[2], 16),
            b: parseInt(kort[3] + kort[3], 16),
        };
    }
    if (lang) {
        return {
            r: parseInt(lang[1], 16),
            g: parseInt(lang[2], 16),
            b: parseInt(lang[3], 16),
        };
    }

    throw new Error(`Geen leesbare kleur: ${hex}`);
};

/**
 * Relatieve luminantie volgens WCAG 2.1.
 *
 * @param {string} hex - '#rgb' of '#rrggbb'
 * @returns {number} 0 (zwart) t/m 1 (wit)
 */
const relativeLuminance = (hex) => {
    const { r, g, b } = parseHex(hex);
    const kanaal = (waarde) => {
        const v = waarde / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * kanaal(r) + 0.7152 * kanaal(g) + 0.0722 * kanaal(b);
};

/**
 * @param {string} kleurA - '#rgb' of '#rrggbb'
 * @param {string} kleurB - '#rgb' of '#rrggbb'
 * @returns {number} De verhouding, 1 t/m 21. Volgorde maakt niet uit.
 */
const contrastRatio = (kleurA, kleurB) => {
    const a = relativeLuminance(kleurA);
    const b = relativeLuminance(kleurB);

    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

export { relativeLuminance };
export default contrastRatio;
